import { __assign, __rest } from "tslib";
import React, { useCallback, useEffect, useRef, useState, useImperativeHandle } from 'react';
import clsx from 'clsx';
import { KeyCode } from '../internal/keycode';
import { getBaseProps } from '../internal/base-component';
import useFocusVisible from '../internal/hooks/focus-visible';
import Portal from '../internal/components/portal';
import PopoverContainer from './container';
import PopoverBody from './body';
import styles from './styles.css.js';
import { fireNonCancelableEvent } from '../internal/events/index';
import mergeRefs from 'react-merge-refs';
export default React.forwardRef(InternalPopover);
function InternalPopover(_a, ref) {
    var _b = _a.position, position = _b === void 0 ? 'right' : _b, _c = _a.size, size = _c === void 0 ? 'medium' : _c, _d = _a.fixedWidth, fixedWidth = _d === void 0 ? false : _d, _e = _a.triggerType, triggerType = _e === void 0 ? 'text' : _e, _f = _a.dismissButton, dismissButton = _f === void 0 ? true : _f, dismissAriaLabel = _a.dismissAriaLabel, children = _a.children, header = _a.header, content = _a.content, _g = _a.renderWithPortal, renderWithPortal = _g === void 0 ? false : _g, __overflowVisible = _a.__overflowVisible, __onOpen = _a.__onOpen, _h = _a.__internalRootRef, __internalRootRef = _h === void 0 ? null : _h, restProps = __rest(_a, ["position", "size", "fixedWidth", "triggerType", "dismissButton", "dismissAriaLabel", "children", "header", "content", "renderWithPortal", "__overflowVisible", "__onOpen", "__internalRootRef"]);
    var baseProps = getBaseProps(restProps);
    var focusVisible = useFocusVisible();
    var triggerRef = useRef(null);
    var popoverRef = useRef(null);
    var popoverContentRef = useRef(null);
    var _j = useState(false), visible = _j[0], setVisible = _j[1];
    var onTriggerClick = useCallback(function () {
        fireNonCancelableEvent(__onOpen);
        setVisible(true);
    }, [__onOpen]);
    var onDismiss = useCallback(function () {
        setVisible(false);
    }, []);
    var onTriggerKeyDown = useCallback(function (event) {
        if (event.keyCode === KeyCode.tab || event.keyCode === KeyCode.escape) {
            setVisible(false);
        }
    }, []);
    useImperativeHandle(ref, function () { return ({
        dismissPopover: onDismiss
    }); });
    useEffect(function () {
        var onDocumentClick = function (event) {
            var _a, _b;
            var target = event.target;
            if (target && !((_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) && !((_b = popoverContentRef.current) === null || _b === void 0 ? void 0 : _b.contains(target))) {
                setVisible(false);
            }
        };
        document.addEventListener('mousedown', onDocumentClick, { capture: true });
        return function () {
            document.removeEventListener('mousedown', onDocumentClick, { capture: true });
        };
    }, []);
    var triggerProps = {
        // https://github.com/microsoft/TypeScript/issues/36659
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref: triggerRef,
        onClick: onTriggerClick,
        onKeyDown: onTriggerKeyDown,
        className: clsx(styles.trigger, styles["trigger-type-" + triggerType])
    };
    var popoverContent = (React.createElement("span", { "aria-live": dismissButton ? undefined : 'polite', "aria-atomic": dismissButton ? undefined : true, ref: popoverContentRef }, visible && triggerRef.current && (React.createElement(PopoverContainer, { position: position, track: triggerRef.current, arrow: function (position) { return (React.createElement("div", { className: clsx(styles.arrow, styles["arrow-position-" + position]) },
            React.createElement("div", { className: styles['arrow-outer'] }),
            React.createElement("div", { className: styles['arrow-inner'] }))); } },
        React.createElement(PopoverBody, { size: size, fixedWidth: fixedWidth, dismissButton: dismissButton, dismissAriaLabel: dismissAriaLabel, header: header, onDismiss: onDismiss, overflowVisible: __overflowVisible ? 'both' : undefined }, content)))));
    return (React.createElement("span", __assign({}, baseProps, { className: clsx(styles.root, baseProps.className), ref: mergeRefs([popoverRef, __internalRootRef]) }),
        triggerType === 'text' ? (React.createElement("button", __assign({}, triggerProps, { type: "button", "aria-haspopup": "dialog" }, focusVisible),
            React.createElement("span", null, children))) : (React.createElement("span", __assign({}, triggerProps), children)),
        renderWithPortal ? React.createElement(Portal, null, popoverContent) : popoverContent));
}
//# sourceMappingURL=internal.js.map